import { c as _c } from "react/compiler-runtime";
import { InstatusStatus, useInstatus } from "@/hooks/useInstatus";
import { getCoreUsageWindow, useCoreUsageTimeseriesNonSuspense, useCurrentCoreUsageNonSuspense } from "@/hooks/useUsage";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { cn } from "@/lib/utils";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { RAIL_WIDTH, RAIL_EXPANDED_WIDTH, RAIL_COLUMN_PADDING } from "./railConstants";
import { memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useLiveMetricsSheetState } from "@/hooks/useLiveMetricsSheetState";
import { useKeyedState } from "@/hooks/useKeyedState";
import { useTheme } from "next-themes";
import vcpu0Light from "@/assets/vCPU_animations/light/vCPU-0-light.png";
import vcpu0Dark from "@/assets/vCPU_animations/dark/vCPU-0-dark.png";
import vcpu1Light from "@/assets/vCPU_animations/light/vCPU-1-light.png";
import vcpu1Dark from "@/assets/vCPU_animations/dark/vCPU-1-dark.png";
import vcpu2Light from "@/assets/vCPU_animations/light/vCPU-2-light.png";
import vcpu2Dark from "@/assets/vCPU_animations/dark/vCPU-2-dark.png";
import vcpu3Light from "@/assets/vCPU_animations/light/vCPU-3-light.png";
import vcpu3Dark from "@/assets/vCPU_animations/dark/vCPU-3-dark.png";
import vcpu4Light from "@/assets/vCPU_animations/light/vCPU-4-light.png";
import vcpu4Dark from "@/assets/vCPU_animations/dark/vCPU-4-dark.png";
import vcpu5Light from "@/assets/vCPU_animations/light/vCPU-5-light.png";
import vcpu5Dark from "@/assets/vCPU_animations/dark/vCPU-5-dark.png";
type StatusPresentation = {
  label: string;
  dotClassName: string;
};

// getStatusPresentation only ever returns one of a handful of fixed strings
// rendered with the same font/styling, so their measured width is stable for
// the lifetime of the tab. Caching by label text lets every StatusWidgetMorph
// remount (the rail remounts on every client-side navigation) after the first
// reuse a known width instead of re-reading scrollWidth — a forced synchronous
// layout flush that's expensive to pay again right as a new page's content
// (e.g. a large chat transcript) is mounting.
//
// Cached values are the *text-only* width: scrollWidth includes the span's
// current padding (pl-2 expanded, pl-0 collapsed), which is subtracted at
// measure time so collapsed and expanded instances populate the same entry.
const statusLabelWidthCache = new Map<string, number>();

// The label's expanded pl-2, added back onto the cached text-only width where
// it is used: maxWidth sizes the border box (box-sizing: border-box), so the
// padding must be included for the full text to fit.
const STATUS_LABEL_EXPANDED_PADDING_PX = 8;
function measureStatusLabelWidth(el: HTMLSpanElement, label: string): number {
  // scrollWidth includes the span's current (possibly mid-transition)
  // padding-left; subtract it so the cached value is padding-independent.
  // Round up to a whole pixel: a fractional reveal width makes the
  // flex-centered dot land on sub-pixel positions each frame, which
  // shimmers against its running pulse animation.
  const paddingLeft = parseFloat(getComputedStyle(el).paddingLeft) || 0;
  const width = Math.ceil(el.scrollWidth - paddingLeft);
  const fontsSettled = !("fonts" in document) || document.fonts.status === "loaded";
  // Only settled measurements are cached: a zero or pre-font-load width
  // would otherwise pin a wrong maxWidth for the rest of the tab session.
  if (width > 0 && fontsSettled) {
    statusLabelWidthCache.set(label, width);
  }
  return width;
}
const getStatusPresentation = (status?: InstatusStatus): StatusPresentation => {
  switch (status) {
    case InstatusStatus.UP:
      return {
        label: "ALL SYSTEMS OPERATIONAL",
        dotClassName: "animate-status-pulse"
      };
    case InstatusStatus.HASISSUES:
      return {
        label: "DEGRADED PERFORMANCE",
        dotClassName: "bg-amber-500"
      };
    case InstatusStatus.UNDERMAINTENANCE:
      return {
        label: "UNDER MAINTENANCE",
        dotClassName: "bg-blue-500"
      };
    default:
      return {
        label: "SYSTEM STATUS UNKNOWN",
        dotClassName: "bg-neutral-500"
      };
  }
};
type VCpuIconProps = {
  totalVCpus: number;
  className?: string;
};
const VCpuIcon = memo(function VCpuIcon(t0) {
  const $ = _c(14);
  const {
    totalVCpus,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [{
      threshold: 5000,
      light: vcpu5Light,
      dark: vcpu5Dark
    }, {
      threshold: 1000,
      light: vcpu4Light,
      dark: vcpu4Dark
    }, {
      threshold: 500,
      light: vcpu3Light,
      dark: vcpu3Dark
    }, {
      threshold: 250,
      light: vcpu2Light,
      dark: vcpu2Dark
    }, {
      threshold: 125,
      light: vcpu1Light,
      dark: vcpu1Dark
    }, {
      threshold: 0,
      light: vcpu0Light,
      dark: vcpu0Dark
    }];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const vcpuImages = t2 as const;
  let t3;
  if ($[1] !== totalVCpus) {
    t3 = vcpuImages.find(t4 => {
      const {
        threshold
      } = t4;
      return totalVCpus >= threshold;
    }) ?? vcpuImages[vcpuImages.length - 1];
    $[1] = totalVCpus;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const selectedImages = t3;
  const t4 = `dark:hidden ${className}`;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      position: "relative",
      zIndex: 50
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== selectedImages.light.src || $[5] !== t4) {
    t6 = <img src={selectedImages.light.src} alt="vCPU" className={t4} style={t5} />;
    $[4] = selectedImages.light.src;
    $[5] = t4;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = `hidden dark:block ${className}`;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      position: "relative",
      zIndex: 50
    };
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== selectedImages.dark.src || $[9] !== t7) {
    t9 = <img src={selectedImages.dark.src} alt="vCPU" className={t7} style={t8} />;
    $[8] = selectedImages.dark.src;
    $[9] = t7;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t6 || $[12] !== t9) {
    t10 = <>{t6}{t9}</>;
    $[11] = t6;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  return t10;
});

/**
 * Mini pixel bar chart canvas component that displays vCPU usage over time.
 * Uses a rolling window to create a time-series effect where old data shifts left
 * and new data appears on the right.
 *
 * Memoized because the parent `StatusIndicator` lives in `Sidebar`, which
 * re-renders on every route change. Without `memo`, the canvas re-runs its
 * draw effect (including `getComputedStyle` / `getBoundingClientRect`) on
 * every menu click even when `values` hasn't moved, contributing to the
 * choppy click feel.
 */
const MiniPixelBarChartCanvas = memo(function MiniPixelBarChartCanvas(t0) {
  const $ = _c(19);
  const {
    values
  } = t0;
  const {
    resolvedTheme
  } = useTheme();
  const barColor = resolvedTheme === "dark" ? "rgba(240,251,41,0.7)" : "#161718";
  const canvasRef = useRef(null);
  const [cssWidth, setCssWidth] = useState(190);
  const [forceRedraw, setForceRedraw] = useState(0);
  const colCount = Math.max(1, Math.floor((cssWidth + 1) / 2));
  let t1;
  if ($[0] !== colCount || $[1] !== values) {
    t1 = values.slice(-colCount);
    $[0] = colCount;
    $[1] = values;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const rollingValues = t1;
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }
      let raf = 0;
      const updateWidth = () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const boundingRect = canvas.getBoundingClientRect();
          const nextWidth = Math.max(1, Math.floor(boundingRect.width));
          setCssWidth(prev => prev === nextWidth ? prev : nextWidth);
        });
      };
      const initialTimeout = setTimeout(() => {
        updateWidth();
      }, 0);
      if (typeof ResizeObserver !== "undefined") {
        const observer = new ResizeObserver(() => {
          updateWidth();
        });
        observer.observe(canvas);
        if (typeof IntersectionObserver !== "undefined") {
          const intersectionObserver = new IntersectionObserver(entries => {
            const isVisible = entries[0]?.isIntersecting;
            if (isVisible) {
              updateWidth();
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  const boundingRect_0 = canvas.getBoundingClientRect();
                  const nextWidth_0 = Math.max(1, Math.floor(boundingRect_0.width));
                  if (nextWidth_0 > 0) {
                    setCssWidth(prev_0 => prev_0 !== nextWidth_0 ? nextWidth_0 : prev_0);
                    setForceRedraw(_temp);
                  }
                });
              });
            }
          }, {
            threshold: 0.01
          });
          intersectionObserver.observe(canvas);
          return () => {
            clearTimeout(initialTimeout);
            cancelAnimationFrame(raf);
            observer.disconnect();
            intersectionObserver.disconnect();
          };
        }
        return () => {
          clearTimeout(initialTimeout);
          cancelAnimationFrame(raf);
          observer.disconnect();
        };
      }
      window.addEventListener("resize", updateWidth);
      return () => {
        clearTimeout(initialTimeout);
        cancelAnimationFrame(raf);
        window.removeEventListener("resize", updateWidth);
      };
    };
    t3 = [];
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== barColor || $[6] !== colCount || $[7] !== cssWidth || $[8] !== rollingValues) {
    t4 = () => {
      const canvas_0 = canvasRef.current;
      if (!canvas_0) {
        return;
      }
      const boundingRect_1 = canvas_0.getBoundingClientRect();
      const actualWidth = Math.max(1, Math.floor(boundingRect_1.width));
      if (cssWidth <= 0 || actualWidth <= 0) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const boundingRect_2 = canvas_0.getBoundingClientRect();
            const nextWidth_1 = Math.max(1, Math.floor(boundingRect_2.width));
            if (nextWidth_1 > 0 && nextWidth_1 !== cssWidth) {
              setCssWidth(nextWidth_1);
              setForceRedraw(_temp2);
            }
          });
        });
        return;
      }
      if (rollingValues.length === 0) {
        return;
      }
      const rowCount = Math.max(1, Math.floor(10));
      const sampled = Array.from({
        length: colCount
      }, (_, i) => {
        const offset = colCount - rollingValues.length;
        const idx = i - offset;
        return idx >= 0 ? rollingValues[idx] : 0;
      });
      const max = Math.max(1e-9, ...sampled);
      const columns = sampled.map(v_1 => {
        const normalized = Math.max(0, Math.min(1, v_1 / max));
        if (normalized <= 0) {
          return 0;
        }
        return Math.max(1, Math.round(normalized * rowCount));
      });
      const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
      const canvasWidth = Math.max(1, Math.floor(cssWidth * dpr));
      const canvasHeight = Math.max(1, Math.floor(19 * dpr));
      canvas_0.width = canvasWidth;
      canvas_0.height = canvasHeight;
      const ctx = canvas_0.getContext("2d", {
        alpha: true
      });
      if (!ctx) {
        return;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.imageSmoothingEnabled = false;
      ctx.clearRect(0, 0, cssWidth, 19);
      ctx.fillStyle = barColor;
      for (let col = 0; col < columns.length; col++) {
        const columnHeight = columns[col] ?? 0;
        const x = col * 2;
        if (x >= cssWidth) {
          break;
        }
        if (columnHeight <= 0) {
          ctx.fillRect(x, 18, 1, 1);
        }
        for (let row = 0; row < columnHeight; row++) {
          const y = 18 - row * 2;
          if (y < 0) {
            break;
          }
          ctx.fillRect(x, y, 1, 1);
        }
      }
    };
    $[5] = barColor;
    $[6] = colCount;
    $[7] = cssWidth;
    $[8] = rollingValues;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== barColor || $[11] !== colCount || $[12] !== cssWidth || $[13] !== forceRedraw || $[14] !== rollingValues) {
    t5 = [rollingValues, colCount, cssWidth, 19, forceRedraw, barColor];
    $[10] = barColor;
    $[11] = colCount;
    $[12] = cssWidth;
    $[13] = forceRedraw;
    $[14] = rollingValues;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  useEffect(t4, t5);
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      width: "100%",
      height: 19
    };
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== cssWidth) {
    t7 = <canvas ref={canvasRef} width={cssWidth} height={19} className="block" style={t6} aria-label="vCPU usage mini chart" role="img" />;
    $[17] = cssWidth;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
});

// Rail expand curve: easeOutQuint-ish, matching AppSwitcherRail's WIPE_TRANSITION
// so the morphing status box and the rail panel decelerate together.
const MORPH_EASE = "cubic-bezier(0.22,1,0.36,1)";

// Single fixed chip size shared by the collapsed and expanded layouts. Rendering
// the vCPU chip at one constant size means it never scales or stretches as the
// box width animates — the two crossfading layers only reposition it, they never
// resize it. The chip renders at the PNG's intrinsic 46×66 dimensions in both
// states.
const CHIP_SIZE = "w-[46px] h-[66px] max-w-none";

// Fixed width of the expanded layer's content: the expanded rail minus its px-2
// column padding (8px each side). Pinning the expanded layer to this width means
// its label row and sparkline are laid out once at their final size and only
// opacity-crossfade in/out — they never scale or stretch while the box width
// animates (the canvas sparkline in particular would otherwise be CSS-scaled off
// its last-drawn bitmap, reading as a horizontal stretch). The rail panel's own
// overflow-hidden clips the excess while the box is mid-width.
const EXPANDED_CONTENT_WIDTH = RAIL_EXPANDED_WIDTH - 2 * RAIL_COLUMN_PADDING;

// Padding on all four sides of the standalone status hover card. Symmetric so
// the content insets match top and sides. The chip graphic carries transparent
// top margin, so its upward overflow clips against this edge without cutting any
// visible pixels. Doubles as the -alignOffset that lifts the card so the widget
// inside lands exactly over the collapsed chip in the trigger underneath.
const STATUS_CARD_PADDING = 8;

// Expand/shrink duration for the standalone status hover card. Close to the
// rail's own 240ms so the pop-out feels snappy rather than sluggish; the card
// holds its mount for this long on close so the shrink fully plays before it
// unmounts (see RailStatusHoverCard).
const STATUS_CARD_MORPH_MS = 220;

// On close, the border/shadow/surface layers fade out during only the tail of
// the shrink (via a transition delay) rather than over the whole duration, so
// they stay visibly solid while the box is still shrinking and finish fading
// to nothing exactly as the width lands at 48px — nothing opaque is left to
// linger, and unmounting immediately after doesn't pop the rail's right
// border back in (it's already invisible). See RailStatusHoverCard.
const STATUS_CARD_CLOSE_FADE_MS = 140;
const STATUS_CARD_CLOSE_FADE_DELAY_MS = STATUS_CARD_MORPH_MS - STATUS_CARD_CLOSE_FADE_MS;

// Geometry of the collapsed widget, shared with RailStatusWidgetSkeleton: the
// rail's footer icon rows stack on top of this, so any height drift between
// the live widget and its twin shifts those rows when the rail swaps in.
const STATUS_WIDGET_STACK = "flex w-full flex-col gap-1.5 pb-1";
const STATUS_WIDGET_BOX = "relative h-[62px] w-full rounded-lg border border-[var(--color-border)] bg-transparent";
const STATUS_WIDGET_COLLAPSED_LAYER = "absolute inset-x-0 top-0 flex h-[58px] items-center justify-center";
const STATUS_WIDGET_DOT_ROW = "flex h-6 w-full items-center justify-center rounded-md";

/**
 * Chip graphic with the vCPU value overlaid on its display window: the entire
 * content of the collapsed layout, so nothing has to shrink to fit the narrow
 * box. The chip renders at CHIP_SIZE, the same fixed size the expanded layer
 * uses, so it never appears to scale as the box morphs.
 */
function CollapsedVCpuChip(t0) {
  const $ = _c(10);
  const {
    totalVCpus,
    value
  } = t0;
  const digits = String(value).length;
  const fontSize = digits >= 5 ? "11px" : digits >= 4 ? "13.5px" : digits >= 3 ? "15px" : "18px";
  let t1;
  if ($[0] !== totalVCpus) {
    t1 = <span className="pointer-events-none -translate-y-[11px]"><VCpuIcon totalVCpus={totalVCpus} className={CHIP_SIZE} /></span>;
    $[0] = totalVCpus;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== fontSize) {
    t2 = {
      letterSpacing: "-1.25px",
      fontSize
    };
    $[2] = fontSize;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2 || $[5] !== value) {
    t3 = <span className="type-numerals-pixel-sm pointer-events-none absolute inset-x-0 bottom-[7px] text-center leading-none text-neutral-800 dark:text-neutral-300" style={t2}>{value}</span>;
    $[4] = t2;
    $[5] = value;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <>{t1}{t3}</>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/**
 * Static twin of the collapsed widget for the rail's loading skeleton: same
 * box, chip, and status-dot row, at the pending values the live widget itself
 * shows before its queries resolve (a `-` reading, status-unknown dot). The
 * rail skeleton can't render the live widget, whose data hooks would fire org
 * queries while auth is still resolving, but the footer rows above it only land
 * in their final position if this stack is the same height.
 */
export function RailStatusWidgetSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className={STATUS_WIDGET_BOX}><div className={STATUS_WIDGET_COLLAPSED_LAYER}><CollapsedVCpuChip totalVCpus={0} value="-" /></div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className={STATUS_WIDGET_STACK}>{t0}<div className={STATUS_WIDGET_DOT_ROW}><span className={cn("inline-block size-[8px] rounded-full", getStatusPresentation(undefined).dotClassName)} /></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/**
 * Unified vCPU / system-status widget that morphs between the collapsed (narrow
 * rail) and expanded (200px rail) states instead of hard-swapping two separate
 * components. The box width is *not* animated here: the widget is `w-full`
 * inside the rail's padded column, so it scales in lock-step with the rail's own
 * width transition for free (and the 8px column padding keeps the side margins
 * locked in both states). Only the per-state content crossfades:
 *
 * - The box is a single fixed height in both states; two absolutely-positioned
 *   layers (collapsed = chip over value, expanded = label row + sparkline)
 *   crossfade, so the height never changes as the width animates.
 * - Expanded-only text (`vCPU USAGE`, the status label) is `whitespace-nowrap`
 *   and fades out fast on close (and in slightly late on open) so it is gone
 *   before the shrinking box could ever wrap or clip it.
 * - The value, chip icon, and status dot exist in both states and persist.
 */
export const StatusWidgetMorph = memo(function StatusWidgetMorph(t0) {
  const $ = _c(79);
  const {
    expanded,
    morphMs: t1,
    suppressHoverFill: t2
  } = t0;
  const morphMs = t1 === undefined ? 240 : t1;
  const suppressHoverFill = t2 === undefined ? false : t2;
  const {
    openLiveMetricsSheet
  } = useLiveMetricsSheetState();
  const {
    status
  } = useInstatus();
  const {
    totalVCpus,
    isLoading: isUsageLoading,
    currentUsage
  } = useCurrentCoreUsageNonSuspense();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUserNonSuspense();
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getCoreUsageWindow(15, 24);
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    startDate,
    endDate
  } = t3;
  const {
    usageSnapshots
  } = useCoreUsageTimeseriesNonSuspense(15, startDate, endDate);
  const statusPresentation = getStatusPresentation(status);
  const statusLabel = statusPresentation.label;
  const statusLabelRef = useRef(null);
  const [measuredStatusLabelWidth, setMeasuredStatusLabelWidth] = useKeyedState(statusLabel, 0);
  const statusLabelWidth = statusLabelWidthCache.get(statusLabel) ?? measuredStatusLabelWidth;
  const statusLabelRefCallback = el => {
    statusLabelRef.current = el;
    if (el && !statusLabelWidthCache.has(statusLabel)) {
      setMeasuredStatusLabelWidth(measureStatusLabelWidth(el, statusLabel));
    }
  };
  let t4;
  if ($[1] !== setMeasuredStatusLabelWidth || $[2] !== statusLabel) {
    t4 = [statusLabel, setMeasuredStatusLabelWidth];
    $[1] = setMeasuredStatusLabelWidth;
    $[2] = statusLabel;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  useEffect(() => {
    if (statusLabelWidthCache.has(statusLabel) || !("fonts" in document) || document.fonts.status === "loaded") {
      return;
    }
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled && statusLabelRef.current) {
        setMeasuredStatusLabelWidth(measureStatusLabelWidth(statusLabelRef.current, statusLabel));
      }
    });
    return () => {
      cancelled = true;
    };
  }, t4);
  let t5;
  if ($[4] !== totalVCpus || $[5] !== usageSnapshots) {
    const values = usageSnapshots?.map(_temp3) ?? [];
    t5 = values.length ? [...values] : [totalVCpus];
    $[4] = totalVCpus;
    $[5] = usageSnapshots;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const chartValues = t5;
  let t6;
  bb0: {
    if (!chartValues || chartValues.length === 0) {
      t6 = false;
      break bb0;
    }
    t6 = chartValues.some(_temp4);
  }
  const hasData = t6;
  const valueLabel = isUsageLoading ? "-" : currentUsage ? totalVCpus : "\u2013";
  let t7;
  if ($[7] !== openLiveMetricsSheet) {
    t7 = () => {
      openLiveMetricsSheet();
    };
    $[7] = openLiveMetricsSheet;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleVcpuClick = t7;
  const t8 = `opacity ${morphMs}ms ${MORPH_EASE}`;
  let t9;
  if ($[9] !== t8) {
    t9 = {
      transition: t8
    };
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const shownLayerStyle = t9;
  let t10;
  if ($[11] !== morphMs) {
    t10 = Math.round(morphMs * 0.75);
    $[11] = morphMs;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const t11 = `opacity ${t10}ms ${MORPH_EASE}`;
  let t12;
  if ($[13] !== t11) {
    t12 = {
      transition: t11
    };
    $[13] = t11;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const hiddenLayerStyle = t12;
  const t13 = STATUS_WIDGET_STACK;
  const t14 = suppressHoverFill ? "transition-none hover:bg-transparent dark:hover:bg-transparent" : "transition-colors hover:bg-black/[0.06] dark:hover:bg-white/[0.04]";
  let t15;
  if ($[15] !== t14) {
    t15 = cn(STATUS_WIDGET_BOX, "cursor-pointer", t14);
    $[15] = t14;
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  const t16 = expanded ? hiddenLayerStyle : shownLayerStyle;
  const t17 = expanded ? "pointer-events-none opacity-0" : "opacity-100";
  let t18;
  if ($[17] !== t17) {
    t18 = cn(STATUS_WIDGET_COLLAPSED_LAYER, t17);
    $[17] = t17;
    $[18] = t18;
  } else {
    t18 = $[18];
  }
  let t19;
  if ($[19] !== totalVCpus || $[20] !== valueLabel) {
    t19 = <CollapsedVCpuChip totalVCpus={totalVCpus} value={valueLabel} />;
    $[19] = totalVCpus;
    $[20] = valueLabel;
    $[21] = t19;
  } else {
    t19 = $[21];
  }
  let t20;
  if ($[22] !== t16 || $[23] !== t18 || $[24] !== t19) {
    t20 = <div style={t16} className={t18}>{t19}</div>;
    $[22] = t16;
    $[23] = t18;
    $[24] = t19;
    $[25] = t20;
  } else {
    t20 = $[25];
  }
  const t21 = expanded ? shownLayerStyle : hiddenLayerStyle;
  let t22;
  if ($[26] !== t21) {
    t22 = {
      ...t21,
      width: EXPANDED_CONTENT_WIDTH
    };
    $[26] = t21;
    $[27] = t22;
  } else {
    t22 = $[27];
  }
  const t23 = expanded ? "opacity-100" : "pointer-events-none opacity-0";
  let t24;
  if ($[28] !== t23) {
    t24 = cn("absolute left-0 top-0 flex h-[58px] flex-col justify-center gap-1 px-3", t23);
    $[28] = t23;
    $[29] = t24;
  } else {
    t24 = $[29];
  }
  let t25;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <span className="type-body-xxs-regular whitespace-nowrap text-neutral-800 dark:text-neutral-300">vCPU USAGE</span>;
    $[30] = t25;
  } else {
    t25 = $[30];
  }
  let t26;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      letterSpacing: "-2px"
    };
    $[31] = t26;
  } else {
    t26 = $[31];
  }
  let t27;
  if ($[32] !== valueLabel) {
    t27 = <span className="type-numerals-pixel-sm text-neutral-800 dark:text-neutral-300" style={t26}>{valueLabel}</span>;
    $[32] = valueLabel;
    $[33] = t27;
  } else {
    t27 = $[33];
  }
  let t28;
  if ($[34] !== totalVCpus) {
    t28 = <span className="pointer-events-none relative z-50 -ml-2 overflow-visible"><VCpuIcon totalVCpus={totalVCpus} className={CHIP_SIZE} /></span>;
    $[34] = totalVCpus;
    $[35] = t28;
  } else {
    t28 = $[35];
  }
  let t29;
  if ($[36] !== t27 || $[37] !== t28) {
    t29 = <div className="flex items-center justify-between">{t25}<span className="relative flex h-6 shrink-0 translate-x-[15px] items-center gap-0 overflow-visible">{t27}{t28}</span></div>;
    $[36] = t27;
    $[37] = t28;
    $[38] = t29;
  } else {
    t29 = $[38];
  }
  let t30;
  if ($[39] !== activeGithubOrg || $[40] !== chartValues || $[41] !== hasData) {
    t30 = <div className="min-h-[19px] w-full">{hasData ? <MiniPixelBarChartCanvas key={`${activeGithubOrg}-morph`} values={chartValues} /> : <div className="flex h-[19px] items-center justify-center"><span className="type-body-xxs-regular text-neutral-500 dark:text-neutral-400">No data</span></div>}</div>;
    $[39] = activeGithubOrg;
    $[40] = chartValues;
    $[41] = hasData;
    $[42] = t30;
  } else {
    t30 = $[42];
  }
  let t31;
  if ($[43] !== t22 || $[44] !== t24 || $[45] !== t29 || $[46] !== t30) {
    t31 = <div style={t22} className={t24}>{t29}{t30}</div>;
    $[43] = t22;
    $[44] = t24;
    $[45] = t29;
    $[46] = t30;
    $[47] = t31;
  } else {
    t31 = $[47];
  }
  let t32;
  if ($[48] !== handleVcpuClick || $[49] !== t15 || $[50] !== t20 || $[51] !== t31) {
    t32 = <button type="button" onClick={handleVcpuClick} aria-label="Show vCPU usage details" className={t15}>{t20}{t31}</button>;
    $[48] = handleVcpuClick;
    $[49] = t15;
    $[50] = t20;
    $[51] = t31;
    $[52] = t32;
  } else {
    t32 = $[52];
  }
  const t33 = "https://status.blacksmith.sh/";
  const t34 = "_blank";
  const t35 = "noreferrer";
  const t36 = "Status & updates (opens in a new tab)";
  const t37 = suppressHoverFill ? "transition-none hover:bg-transparent dark:hover:bg-transparent" : "transition-colors hover:bg-black/[0.06] dark:hover:bg-white/[0.04]";
  let t38;
  if ($[53] !== t37) {
    t38 = cn(STATUS_WIDGET_DOT_ROW, "cursor-pointer", t37);
    $[53] = t37;
    $[54] = t38;
  } else {
    t38 = $[54];
  }
  const t39 = "flex items-center";
  const t40 = status === InstatusStatus.UP ? <span className="relative inline-flex transform-gpu items-center justify-center will-change-transform [backface-visibility:hidden]"><span className={cn("absolute size-[12px] rounded-full animate-status-pulse-background", statusPresentation.dotClassName)} style={{
      backgroundColor: "#4CD137"
    }} /><span className="relative inline-block size-[6px] rounded-full animate-status-pulse" style={{
      backgroundColor: "#4CD137"
    }} /></span> : <span className={cn("inline-block size-[8px] transform-gpu rounded-full will-change-transform [backface-visibility:hidden]", statusPresentation.dotClassName)} />;
  const t41 = expanded && statusLabelWidth > 0 ? statusLabelWidth + STATUS_LABEL_EXPANDED_PADDING_PX : 0;
  const t42 = `max-width ${morphMs}ms ${MORPH_EASE}, opacity ${morphMs}ms ${MORPH_EASE}, padding ${morphMs}ms ${MORPH_EASE}`;
  let t43;
  if ($[55] !== t41 || $[56] !== t42) {
    t43 = {
      maxWidth: t41,
      transition: t42,
      transitionDelay: "0ms"
    };
    $[55] = t41;
    $[56] = t42;
    $[57] = t43;
  } else {
    t43 = $[57];
  }
  const t44 = expanded ? "pl-2 opacity-100" : "pointer-events-none pl-0 opacity-0";
  let t45;
  if ($[58] !== t44) {
    t45 = cn("type-body-xxs-regular whitespace-nowrap text-neutral-800 dark:text-neutral-300", t44);
    $[58] = t44;
    $[59] = t45;
  } else {
    t45 = $[59];
  }
  let t46;
  if ($[60] !== statusLabelRefCallback || $[61] !== statusPresentation.label || $[62] !== t43 || $[63] !== t45) {
    t46 = <span ref={statusLabelRefCallback} style={t43} className={t45}>{statusPresentation.label}</span>;
    $[60] = statusLabelRefCallback;
    $[61] = statusPresentation.label;
    $[62] = t43;
    $[63] = t45;
    $[64] = t46;
  } else {
    t46 = $[64];
  }
  const t47 = expanded ? 14 : 0;
  const t48 = `width ${morphMs}ms ${MORPH_EASE}`;
  let t49;
  if ($[65] !== t47 || $[66] !== t48) {
    t49 = <span aria-hidden={true} className="pointer-events-none shrink-0" style={{
      width: t47,
      transition: t48,
      transitionDelay: "0ms"
    }} />;
    $[65] = t47;
    $[66] = t48;
    $[67] = t49;
  } else {
    t49 = $[67];
  }
  let t50;
  if ($[68] !== t40 || $[69] !== t46 || $[70] !== t49) {
    t50 = <span className={t39}>{t40}{t46}{t49}</span>;
    $[68] = t40;
    $[69] = t46;
    $[70] = t49;
    $[71] = t50;
  } else {
    t50 = $[71];
  }
  let t51;
  if ($[72] !== t38 || $[73] !== t50) {
    t51 = <a href={t33} target={t34} rel={t35} aria-label={t36} className={t38}>{t50}</a>;
    $[72] = t38;
    $[73] = t50;
    $[74] = t51;
  } else {
    t51 = $[74];
  }
  let t52;
  if ($[75] !== t13 || $[76] !== t32 || $[77] !== t51) {
    t52 = <div className={t13}>{t32}{t51}</div>;
    $[75] = t13;
    $[76] = t32;
    $[77] = t51;
    $[78] = t52;
  } else {
    t52 = $[78];
  }
  return t52;
});

/**
 * Interactive hover overlay for the vCPU / system-status widget in the slim
 * app-switcher rail. The collapsed chip stays in the 48px rail; hovering it
 * pops a wider card over the contextual sidebar that morphs the *same* widget
 * from its collapsed to expanded layout using the same curve as the rail's full
 * expansion — so only this portion grows, not the whole rail.
 *
 * The card mounts at the collapsed rail width directly over the trigger, then
 * expands on the next frame so the width transition and the widget's own layer
 * crossfade play in lock-step. On close, `morphed` flips back to collapsed:
 * the width shrinks over the full STATUS_CARD_MORPH_MS, while the border,
 * shadow, and colored surface stay solid through most of that shrink and only
 * fade during its tail (see STATUS_CARD_CLOSE_FADE_MS) — so the box visibly
 * shrinks first, then dissolves right as it reaches the chip, instead of
 * either fading in place before it visibly shrinks or leaving a solid box
 * sitting at the collapsed size after the shrink is done. Both finish at the
 * same instant, so unmounting right after doesn't pop the rail's right border
 * back in (nothing opaque is left to pop). overflow-hidden clips the expanded
 * layer's rightward spill mid-morph, exactly as the rail panel does; the
 * top/bottom padding gives the chip graphic the same room to overflow its box
 * that it has in the rail.
 */
export function RailStatusHoverCard(t0) {
  const $ = _c(36);
  const {
    railWidth: t1
  } = t0;
  const railWidth = t1 === undefined ? RAIL_WIDTH : t1;
  const [open, setOpen] = useState(false);
  const [morphed, setMorphed] = useState(false);
  const closeTimerRef = useRef(null);
  const openRafRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      if (openRafRef.current) {
        cancelAnimationFrame(openRafRef.current);
        openRafRef.current = null;
      }
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const clearPending = t2;
  let t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => clearPending;
    t4 = [clearPending];
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = next => {
      clearPending();
      if (next) {
        setOpen(true);
        openRafRef.current = requestAnimationFrame(() => {
          openRafRef.current = requestAnimationFrame(() => setMorphed(true));
        });
      } else {
        setMorphed(false);
        closeTimerRef.current = setTimeout(() => setOpen(false), STATUS_CARD_MORPH_MS + 40);
      }
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const handleOpenChange = t5;
  const t6 = morphed ? 0 : 1;
  let t7;
  if ($[4] !== t6) {
    t7 = {
      opacity: t6,
      transition: `opacity ${STATUS_CARD_MORPH_MS}ms ${MORPH_EASE}`
    };
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <StatusWidgetMorph expanded={false} suppressHoverFill={true} />;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== t7) {
    t9 = <HoverCardTrigger asChild={true}><div className="w-full" style={t7}>{t8}</div></HoverCardTrigger>;
    $[7] = t7;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const t10 = -(railWidth - RAIL_COLUMN_PADDING);
  let t11;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      width: RAIL_EXPANDED_WIDTH
    };
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  const t12 = morphed ? RAIL_EXPANDED_WIDTH : railWidth;
  const t13 = morphed ? 1 : 0;
  const t14 = morphed ? `width ${STATUS_CARD_MORPH_MS}ms ${MORPH_EASE}, opacity ${STATUS_CARD_MORPH_MS}ms ${MORPH_EASE}` : `width ${STATUS_CARD_MORPH_MS}ms ${MORPH_EASE}, opacity ${STATUS_CARD_CLOSE_FADE_MS}ms ${MORPH_EASE} ${STATUS_CARD_CLOSE_FADE_DELAY_MS}ms`;
  const t15 = morphed ? "auto" : "none";
  let t16;
  if ($[10] !== t12 || $[11] !== t13 || $[12] !== t14 || $[13] !== t15) {
    t16 = {
      width: t12,
      opacity: t13,
      transition: t14,
      willChange: "width, opacity",
      pointerEvents: t15
    };
    $[10] = t12;
    $[11] = t13;
    $[12] = t14;
    $[13] = t15;
    $[14] = t16;
  } else {
    t16 = $[14];
  }
  const t17 = morphed ? 1 : 0;
  const t18 = morphed ? `opacity ${STATUS_CARD_MORPH_MS}ms ${MORPH_EASE}` : `opacity ${STATUS_CARD_CLOSE_FADE_MS}ms ${MORPH_EASE} ${STATUS_CARD_CLOSE_FADE_DELAY_MS}ms`;
  let t19;
  if ($[15] !== t17 || $[16] !== t18) {
    t19 = {
      opacity: t17,
      transition: t18
    };
    $[15] = t17;
    $[16] = t18;
    $[17] = t19;
  } else {
    t19 = $[17];
  }
  let t20;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = cn("pointer-events-none absolute inset-0 rounded-lg", "border border-[var(--color-border)]", "shadow-[8px_0px_20px_0px_rgba(0,0,0,0.08)] dark:shadow-[8px_0px_20px_0px_rgba(0,0,0,0.45)]");
    $[18] = t20;
  } else {
    t20 = $[18];
  }
  let t21;
  if ($[19] !== t19) {
    t21 = <div aria-hidden={true} style={t19} className={t20} />;
    $[19] = t19;
    $[20] = t21;
  } else {
    t21 = $[20];
  }
  let t22;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      padding: STATUS_CARD_PADDING
    };
    $[21] = t22;
  } else {
    t22 = $[21];
  }
  const t23 = !morphed;
  let t24;
  if ($[22] !== morphed || $[23] !== t23) {
    t24 = <div style={t22} className="relative overflow-hidden rounded-lg bg-sidebar"><StatusWidgetMorph expanded={morphed} morphMs={STATUS_CARD_MORPH_MS} suppressHoverFill={t23} /></div>;
    $[22] = morphed;
    $[23] = t23;
    $[24] = t24;
  } else {
    t24 = $[24];
  }
  let t25;
  if ($[25] !== t16 || $[26] !== t21 || $[27] !== t24) {
    t25 = <div style={t16} className="relative">{t21}{t24}</div>;
    $[25] = t16;
    $[26] = t21;
    $[27] = t24;
    $[28] = t25;
  } else {
    t25 = $[28];
  }
  let t26;
  if ($[29] !== t10 || $[30] !== t25) {
    t26 = <HoverCardContent side="right" align="start" sideOffset={t10} alignOffset={-STATUS_CARD_PADDING} avoidCollisions={false} style={t11} className="pointer-events-none border-0 bg-transparent p-0 shadow-none dark:bg-transparent">{t25}</HoverCardContent>;
    $[29] = t10;
    $[30] = t25;
    $[31] = t26;
  } else {
    t26 = $[31];
  }
  let t27;
  if ($[32] !== open || $[33] !== t26 || $[34] !== t9) {
    t27 = <HoverCard open={open} onOpenChange={handleOpenChange} openDelay={30} closeDelay={80}>{t9}{t26}</HoverCard>;
    $[32] = open;
    $[33] = t26;
    $[34] = t9;
    $[35] = t27;
  } else {
    t27 = $[35];
  }
  return t27;
}
function _temp(v) {
  return v + 1;
}
function _temp2(v_0) {
  return v_0 + 1;
}
function _temp3(p) {
  return Number.isFinite(p.total?.vcpus) ? p.total.vcpus : 0;
}
function _temp4(v) {
  return v > 0;
}